import { GridCellParams } from '@mui/x-data-grid-pro';
import Chip from '@mui/material/Chip';
import { v4 } from 'uuid';
import { GridCustomColDef } from '../../types';
import {
  advancedOperators,
  advancedDatePickerOperator,
  advancedNumericOperators,
  advancedEnumOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { formatPrice, getFormattedDate, chipStyles, getDate } from '../../../utils/helpers';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { getCommissionsInfoByType } from '../../../store/GeneralStore';
import { Payment } from '../../../store/Payment';
import { PaymentRecipientType } from '../../../store/PaymentRecipient';
import { Text } from '../../Atoms/Text';
import { handleAssetClick } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';

export const DefaultPaymentGridColumnDefs = (): GridCustomColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'details',
    headerName: '',
    sortable: false,
    width: 65,
    filterable: false,
    renderCell: (params: GridCellParams<Payment>) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
        state={{
          id: v4(),
          data: params.row,
          ...getMetaData({ name: ComponentName.PAYMENT, id: params.row.id, data: params.row })
        }}
      />
    ),
    hideable: false,
    pinned: 'left',
    disableReorder: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'date',
    headerName: 'Date',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    description: 'Drag to move column',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'amount',
    headerName: 'Amount',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'recipientName',
    headerName: 'Recipient',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: Object.values(PaymentRecipientType ?? {}),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'recipientType',
    headerName: 'Recipient Type',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'assetName',
    headerName: 'Asset',
    renderCell: (params: GridCellParams<Payment>) => {
      if (!params.row.assetName) return <></>;
      return (
        <button
          className="flex items-center"
          type="button"
          onClick={() => handleAssetClick(params.row.assetId, ComponentName.ASSET)}>
          <Text
            size="sm14"
            color="indigo">
            {params.row.assetName as string}
          </Text>
        </button>
      );
    },
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('asset', 'commissionsStatus'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionsStatus',
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    },
    headerName: 'Commissions Status',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('account', 'suppliers'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'supplierName',
    headerName: 'Supplier',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('account', 'salesAgents'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'salesAgentName',
    headerName: 'Sales Agent',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'customerName',
    headerName: 'Customer',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'createdAt',
    headerName: 'Created At',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator(),
    valueFormatter: (value) => {
      return getFormattedDate(value, 'lll');
    },
    valueGetter: (value) => {
      return value ? getDate(value).getTime() : '';
    },
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'createdBy.name',
    headerName: 'Created By',
    sortable: true,
    filterable: true,
    minWidth: 170,
    flex: 1,
    hideByDefault: true,
    description: 'Drag to move column',
    renderCell: (params: GridCellParams) => (
      <span title={params.row.createdBy?.email || ''}>{params.value as string}</span>
    ),
    valueGetter: (_value, row) => `${row?.createdBy?.firstName || ''} ${row?.createdBy?.lastName || ''}`,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'updatedAt',
    headerName: 'Modified At',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator(),
    valueFormatter: (value) => {
      return getFormattedDate(value, 'lll');
    },
    valueGetter: (value) => {
      return value ? getDate(value).getTime() : '';
    },
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'modifiedBy.name',
    headerName: 'Modified By',
    sortable: true,
    filterable: true,
    minWidth: 170,
    flex: 1,
    hideByDefault: true,
    description: 'Drag to move column',
    renderCell: (params: GridCellParams) => (
      <span title={params.row.modifiedBy?.email || ''}>{params.value as string}</span>
    ),
    valueGetter: (_value, row) => `${row?.modifiedBy?.firstName || ''} ${row?.modifiedBy?.lastName || ''}`,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'id',
    headerName: 'ID',
    sortable: true,
    filterable: true,
    minWidth: 250,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators(),
    hideByDefault: true
  }
];
